import {
  Component,
  Prop,
  Event,
  EventEmitter,
  Method,
  State,
  Element,
  h
} from '@stencil/core';
import { Color, Size } from '../../interface';
import { i18n } from '../../utils/i18n';
import { DataIn, DataOut, DeviceStatus, LockdownState, DoorStatus, SwitchState } from './2n-commander-door.interface';

@Component({
  tag: 'lar-2n-commander-door',
  styleUrl: '2n-commander-door.scss',
  shadow: true
})
export class TwoNCommanderDoor {
  @Element() el!: HTMLElement;
  
  /////// LarvaNode base properties
  @Prop({ mutable: true }) icon?: string = 'door';
  @Prop({ mutable: true }) color?: Color = 'primary';
  @Prop({ mutable: true }) colorModal?: Color;
  @Prop({ mutable: true }) colorInputs?: Color;
  @Prop() colorIconSmall?: Color;
  @Prop() allowIndicationAutoColoring: boolean = false;
  @Prop() hideTitles?: boolean;
  @Prop() supTitle?: string;
  @Prop() subTitle?: string;
  @Prop() log: boolean = false;
  @Prop() mainTitle!: string;
  @Prop() nodeSize?: Size = 'default';
  @Prop() options?: any;
  
  @Event() output!: EventEmitter<DataOut>;
  @Event() request!: EventEmitter;
  /////// LarvaNode base properties - end

  @State() loading: boolean = true;
  
  @State() err?: string;
  @State() status: DeviceStatus = 'Unknown';
  @State() lockdown: LockdownState = 'Unknown';
  @State() doorState: DoorStatus = 'Unknown';
  @State() doorSwitch?: number;
  @State() doorSwitchState: SwitchState = 'Unknown';

  private node!: HTMLLarNodeElement;

  @Method()
  async input(data: DataIn) {
    this.loading = false;
    this.err = data?.err;
    this.status = (data?.status as DeviceStatus) ?? 'Unknown';
    this.lockdown = (data?.lockdown as LockdownState) ?? 'Unknown';
    this.doorState = (data?.doorState as DoorStatus) ?? 'Unknown';
    this.doorSwitch = data?.doorSwitch;
    this.doorSwitchState = (data?.doorSwitchState as SwitchState) ?? 'Unknown';
  }

  componentDidLoad() {
    const el = this.el.shadowRoot || this.el;
    this.node = el.querySelector('lar-node') as HTMLLarNodeElement;
    this.loading = true;
    this.output.emit();
  }

  handleToggle() {
    if (this.doorSwitch === undefined) return;
    
    // Request: doorSwitchState is 'True' (Unlocked) then send "Off" (Lock), else "On" (Unlock)
    const action = this.doorSwitchState === 'True' ? 'Off' : 'On';
    
    this.output.emit({
        doorSwitch: action
    });
    this.loading = true;
  }

  render() {
    const isLockdown = this.lockdown === 'Lockdown' || this.lockdown === 'PartialLockdown';
    const isUnlocked = this.doorSwitchState === 'True';
    const hasError = !!this.err;
    
    // Icon Logic
    let iconSmall = 'door';
    if (hasError || isLockdown) {
      iconSmall = 'warning';
    } else if (this.doorState !== undefined) {
        iconSmall = isUnlocked ? 'unlock' : 'lock';
    } else {
        // Feedback based on Door Status if switch not present
        if (this.doorState === 'Opened') iconSmall = 'unlock';
        else if (this.doorState === 'Closed') iconSmall = 'lock';
    }
    
    // Value Text Logic
    let value: string = this.status; 
    if (hasError) {
      value = this.err || 'door.error';
    } else if (isLockdown) {
      value = i18n.t(`twoNCommanderDoor.lockdown.${this.lockdown}`);
    } else {
      if (this.doorState !== 'Unknown' && this.doorSwitchState !== 'Unknown') {
        value = i18n.t('twoNCommanderDoor.format', { 
          doorSwitchState: i18n.t(`twoNCommanderDoor.doorSwitchState.${this.doorSwitchState}`), 
          doorState: i18n.t(`twoNCommanderDoor.doorState.${this.doorState}`) 
        });
      } else if (this.doorSwitchState !== 'Unknown') {
        value = i18n.t(`twoNCommanderDoor.doorSwitchState.${this.doorSwitchState}`);
      } else if (this.doorState !== 'Unknown') {
        value = i18n.t(`twoNCommanderDoor.doorState.${this.doorState}`);
      }
    }

    // Color Logic
    let calculatedColor: Color = this.colorIconSmall;
    if (this.allowIndicationAutoColoring && !this.loading) {
        if (hasError || isLockdown) {
          calculatedColor = 'danger';
        } else if (isUnlocked) {
          calculatedColor = 'danger'; 
        } else if (this.status === 'Online') {
          calculatedColor = 'success';
        } else {
          calculatedColor = 'medium';
        }
    }
    const colorIconSmall = this.allowIndicationAutoColoring ? calculatedColor : this.colorIconSmall;

    // Button Props
    const showButton = this.doorSwitch !== undefined;
    const buttonDisabled = this.loading || hasError || isLockdown || this.status !== 'Online';
    
    // If Unlocked (True) -> Button should Lock -> Label 'Lock', Icon 'Lock'
    // If Locked (False/Unknown) -> Button should Unlock -> Label 'Unlock', Icon 'Unlock'
    const buttonLabel = isUnlocked ? 'door.lock' : 'door.unlock';
    const buttonIcon = isUnlocked ? 'lock' : 'unlock';

    const componentProps = showButton ? {
      onClick: () => this.handleToggle(),
      value: buttonLabel,
      icon: buttonIcon,
      disabled: buttonDisabled,
      color: this.colorInputs || this.color
    } : undefined;

    return (
      <lar-node
        value={value}
        hideTitles={this.hideTitles}
        iconSmall={iconSmall}
        colorIconSmall={colorIconSmall}
        icon={this.icon}
        color={this.color}
        supTitle={this.supTitle}
        mainTitle={this.mainTitle}
        subTitle={this.subTitle}
        colorModal={this.colorModal}
        colorInputs={this.colorInputs}
        nodeSize={this.nodeSize}
        log={this.log}
        loading={this.loading}
        component={showButton ? "lar-2n-commander-door-content" : undefined}
        componentProps={componentProps}
        >
        <slot />
      </lar-node>
    );
  }
}
